import React, { useState } from "react";
import { Star, ClockCounterClockwise, ArrowSquareOut, Bell, BellRinging } from "@phosphor-icons/react";
import StockList from "./StockList";
import PriceAlertModal from "../PriceAlertModal";
import { tradingViewStockUrl } from "../../lib/tradingview";

const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    : "—";

export function ClosedView({ items, onOpen }) {
  return (
    <StockList
      items={items || []}
      onOpen={onOpen}
      testIdPrefix="closed-row"
      testid="closed-list"
      emptyMessage="No closed trades yet — as breakouts hit Target or SL they'll appear here as history."
      rowProps={(s) => ({
        typeBadge:
          s.status === "target_hit"
            ? { label: "Target ✓", tone: "pos" }
            : s.status === "sl_hit"
            ? { label: "SL", tone: "neg" }
            : { label: "Exit", tone: "muted" },
        rightHint:
          s.pnl_pct != null
            ? `${s.pnl_pct >= 0 ? "+" : ""}${s.pnl_pct.toFixed(2)}%`
            : undefined,
      })}
    />
  );
}

export function WatchlistView({ items, onOpen, onWatch, watchedSet, onReload }) {
  const [alertItem, setAlertItem] = useState(null);

  if (!items.length) {
    return (
      <div className="p-10 border border-subtle rounded-sm bg-surface text-center" data-testid="watchlist-empty">
        <Star size={24} className="mx-auto text-muted-fg mb-3" />
        <div className="text-primary-fg font-medium mb-1">Your watchlist is empty</div>
        <div className="text-sm text-muted-fg">
          Star any breakout signal to track its live status here.
        </div>
      </div>
    );
  }

  // Items that have an active breakout signal get the universal row.
  // Items without one (user followed a non-breakout symbol) get the
  // lightweight no-signal card. Both share the same alert-set affordance.
  const withSignal = items.filter((w) => w.signal);
  const withoutSignal = items.filter((w) => !w.signal);

  return (
    <>
      <div className="mb-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-fg">
        <span data-testid="watchlist-count">{items.length} symbol{items.length > 1 ? "s" : ""} tracked</span>
        <span className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-pos animate-pulse" />
          live · auto-refresh 60s · click <Bell size={11} weight="bold" className="inline" /> to set alert
        </span>
      </div>

      {withSignal.length > 0 && (
        <StockList
          items={withSignal.map((w) => ({
            ...w.signal,
            // Surface live P&L on the row's right hint
            _live_pnl_pct: w.pnl_pct,
            _live_ltp: w.ltp,
            _alert_price: w.alert_price,
            _watch_symbol: w.symbol,
            noteText: w.note || undefined,
          }))}
          onOpen={onOpen}
          onWatch={onWatch}
          watchedSet={watchedSet}
          testIdPrefix="watch-row"
          testid="watchlist-list"
          rowProps={(s) => ({
            rightHint:
              s._live_pnl_pct != null
                ? `${s._live_pnl_pct >= 0 ? "+" : ""}${s._live_pnl_pct.toFixed(2)}% live`
                : undefined,
          })}
        />
      )}

      {withoutSignal.length > 0 && (
        <div className="mt-6" data-testid="watchlist-nosig-section">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg mb-3 border-b border-subtle pb-2">
            /// Followed symbols · no active breakout
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="watchlist-grid-nosig">
            {withoutSignal.map((w) => (
              <NoSignalWatchCard key={w.symbol} item={w} onAlert={() => setAlertItem(w)} />
            ))}
          </div>
        </div>
      )}

      <PriceAlertModal
        open={!!alertItem}
        item={alertItem}
        onClose={() => setAlertItem(null)}
        onSaved={onReload}
      />
    </>
  );
}

function NoSignalWatchCard({ item, onAlert }) {
  const sym = item.symbol.replace(".NS", "");
  const pos = (item.pnl_pct ?? 0) >= 0;
  const hasLive = item.ltp != null;
  const hasAlert = !!item.alert_price;
  return (
    <div
      className="p-4 border border-subtle bg-surface rounded-sm flex flex-col gap-3 relative"
      data-testid={`watch-nosig-${item.symbol}`}
    >
      <button
        type="button"
        onClick={onAlert}
        title={hasAlert ? `Alert at ₹${item.alert_price} (${item.alert_direction})` : "Set price alert"}
        data-testid={`alert-chip-${item.symbol}`}
        className={
          "absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-sm border text-[10px] font-mono tabular-nums transition " +
          (hasAlert
            ? "bg-pos-soft border-pos text-pos hover:bg-pos-soft"
            : "bg-app border-subtle text-muted-fg hover:text-pos hover:border-pos/40")
        }
      >
        {hasAlert ? <BellRinging size={10} weight="fill" /> : <Bell size={10} weight="bold" />}
        {hasAlert ? `₹${item.alert_price}` : "Set alert"}
      </button>
      <div className="flex items-start justify-between mt-6">
        <div>
          <div className="font-mono text-lg font-bold text-primary-fg">{sym}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mt-0.5">
            No active breakout signal
          </div>
        </div>
        <a
          href={tradingViewStockUrl(item.symbol)}
          target="_blank"
          rel="noreferrer"
          title={`Open ${sym} on TradingView`}
          className="text-muted-fg hover:text-pos transition p-1 -mr-1"
          data-testid={`watch-tv-${item.symbol}`}
        >
          <ArrowSquareOut size={14} weight="bold" />
        </a>
      </div>
      {hasLive ? (
        <div className="grid grid-cols-2 gap-2 font-mono text-xs">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-muted-fg">LTP</div>
            <div className="text-primary-fg tabular-nums">₹{fmt(item.ltp)}</div>
          </div>
          {item.entry_price ? (
            <div>
              <div className="text-[9px] uppercase tracking-widest text-muted-fg">Entry → P&L</div>
              <div className={"tabular-nums font-semibold " + (pos ? "text-pos" : "text-neg")}>
                ₹{fmt(item.entry_price)} · {pos ? "+" : ""}
                {item.pnl_pct != null ? item.pnl_pct.toFixed(2) : "—"}%
              </div>
            </div>
          ) : (
            <div className="text-muted-fg text-[10px] self-end">No entry recorded</div>
          )}
        </div>
      ) : (
        <div className="text-[11px] text-muted-fg italic">Live quote unavailable.</div>
      )}
      {item.note && (
        <div
          className="text-[11px] text-secondary-fg italic border-l-2 border-pos/40 pl-2 flex items-start gap-1"
          title={item.note}
          data-testid={`watch-note-${item.symbol}`}
        >
          <span className="text-pos shrink-0">✎</span>
          <span className="line-clamp-2">{item.note}</span>
        </div>
      )}
    </div>
  );
}

export function EmptyState({ onRun, lastScan }) {
  return (
    <div className="p-10 border border-subtle rounded-sm bg-surface text-center" data-testid="empty-state">
      <ClockCounterClockwise size={28} className="mx-auto text-muted-fg mb-3" />
      <div className="text-primary-fg font-medium mb-1">No breakouts yet</div>
      <div className="text-sm text-muted-fg mb-4">
        {lastScan
          ? "The last scan did not find any qualifying breakouts. Try running a fresh scan."
          : "Scanner has not run yet. Kick it off to populate signals."}
      </div>
      <button
        type="button"
        onClick={onRun}
        className="px-4 py-2 bg-white text-black rounded-sm font-mono text-xs uppercase tracking-widest"
      >
        Run Scan Now
      </button>
    </div>
  );
}
